import React, { useMemo } from "react";

import { debounce } from "lodash-es";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";

import { analyticsService } from "@/common/services/analytics/analytics.service";

import "yet-another-react-lightbox/styles.css";

type ProductImagesLightBoxImage = {
  url: string;
  width?: number | null;
  height?: number | null;
};

type ProductImagesLightBoxProps = {
  productId: string;
  images: ProductImagesLightBoxImage[];
  isOpen: boolean;
  onClose: () => void;
  imageIndex?: number;
};

const ProductImagesLightBox = ({
  images,
  isOpen,
  onClose,
  imageIndex,
}: ProductImagesLightBoxProps) => {
  const slides = useMemo(() => {
    return images.map(({ url, width, height }) => ({
      ...(width && { width }),
      ...(height && { height }),
      src: url,
    }));
  }, [images]);

  const handleEnteredView = () => {
    /** Trigger `image_browse_pdp` event. */
    analyticsService.trackImageBrowsePdp();
  };

  const handleZoom = (imageIndex?: number) => () => {
    /** Trigger `image_zoom_pdp` event. */
    analyticsService.trackImageZoomPdp((imageIndex ?? 0) + 1);
  };

  return (
    <div className="z-50">
      <Lightbox
        open={isOpen}
        close={onClose}
        slides={slides}
        plugins={[Zoom]}
        index={imageIndex}
        on={{
          view: handleEnteredView,
          /** Debounce zoom event so that it doesn't trigger excessively - we use this one for analytics. */
          zoom: debounce(handleZoom(imageIndex), 250),
        }}
        render={{
          slide: ({ slide }) => (
            <div className="flex h-full w-full flex-col items-center justify-center space-y-4">
              {/* eslint-disable-next-line @next/next/no-img-element,jsx-a11y/alt-text */}
              <img
                src={slide.src}
                draggable={false}
                className="yarl__slide_image !max-h-[calc(100vh-120px)] max-w-full object-contain"
              />
            </div>
          ),
        }}
      />
    </div>
  );
};

export default ProductImagesLightBox;
